import React from 'react';
import { motion } from 'framer-motion';
import { X, Play, Pause, Save, Timer, ChevronRight, Target, Brain, Heart, Calendar } from 'lucide-react';
import { WorkoutProgram } from '../../types/workouts';

interface ProgramDetailProps {
  program: WorkoutProgram;
  onClose: () => void;
}

export function ProgramDetail({ program, onClose }: ProgramDetailProps) {
  return (
    <div className="fixed inset-0 bg-background/95 backdrop-blur-xl z-[100] overflow-y-auto pb-24">
      {/* Header Image */}
      <div className="relative h-64 sm:h-96">
        <img
          src={program.image}
          alt={program.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent" />
        
        {/* Back Button */}
        <div className="absolute top-0 left-0 right-0 p-4 bg-gradient-to-b from-background/80 to-transparent">
          <button
            onClick={onClose}
            className="p-2 rounded-lg bg-background/20 backdrop-blur-sm hover:bg-background/40 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="relative -mt-32">
        <div className="container mx-auto px-4 space-y-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="theme-card p-6 space-y-6"
          >
            {/* Title Section */}
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold">{program.name}</h1>
                <p className="text-muted-foreground">{program.description}</p>
              </div>
              <div className="flex items-center gap-2">
                <button className="theme-button px-6 py-3 rounded-lg flex items-center gap-2">
                  <Play className="w-5 h-5" />
                  Start Program
                </button>
              </div>
            </div>

            {/* Info Grid */}
            <div className="grid grid-cols-3 gap-6">
              <div className="col-span-2 space-y-6">
                {/* Program Overview */}
                <div className="theme-card p-6">
                  <h3 className="text-lg font-semibold mb-4">Program Overview</h3>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="space-y-1">
                      <div className="text-sm text-muted-foreground">Duration</div>
                      <div className="font-medium">{program.duration}</div>
                    </div>
                    <div className="space-y-1">
                      <div className="text-sm text-muted-foreground">Level</div>
                      <div className="font-medium">{program.level}</div>
                    </div>
                    <div className="space-y-1">
                      <div className="text-sm text-muted-foreground">Workouts/Week</div>
                      <div className="font-medium">{program.schedule.workoutsPerWeek}x</div>
                    </div>
                    <div className="space-y-1">
                      <div className="text-sm text-muted-foreground">Category</div>
                      <div className="font-medium">{program.category}</div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <h4 className="font-medium">Program Goals</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {program.goals.map((goal, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-2 text-sm bg-muted/30 p-2 rounded-lg"
                        >
                          <Target className="w-4 h-4 text-primary" />
                          <span>{goal}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Program Features */}
                <div className="theme-card p-6">
                  <h3 className="text-lg font-semibold mb-4">Features</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {program.features.map((feature, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-2 text-sm"
                      >
                        <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                {/* Coach Info */}
                <div className="theme-card p-6">
                  <h3 className="text-sm font-medium mb-4">Your Coach</h3>
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={program.coach.image}
                      alt={program.coach.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div>
                      <div className="font-medium">{program.coach.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {program.coach.specialties[0]}
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {program.coach.specialties.slice(1).map((specialty, index) => (
                      <div
                        key={index}
                        className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full inline-block mr-2"
                      >
                        {specialty}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Equipment Needed */}
                <div className="theme-card p-6">
                  <h3 className="text-sm font-medium mb-4">Equipment Needed</h3>
                  <div className="space-y-2">
                    {program.equipment.map((item, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-2 text-sm"
                      >
                        <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Workouts */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Program Workouts</h3>
              <div className="space-y-3">
                {program.workouts.map((workout) => (
                  <motion.div
                    key={workout.id}
                    className="bg-muted/30 p-4 rounded-lg flex items-center justify-between group hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Play className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <div className="font-medium">{workout.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {workout.duration}min • {workout.intensity} Intensity
                        </div>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}